import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft, CheckCircle, XCircle, ArrowRight,
  Clock, User, FileText, Shield, History, RotateCcw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import EditRequestDiff from '../../components/EditRequestDiff';
import OTPVerificationCompact from '../../components/OTPVerificationCompact';

const EditRequestView = () => {
  const { id: editRequestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [comments, setComments] = useState('');
  const [forwardToLevel, setForwardToLevel] = useState('');
  const [forwardToUser, setForwardToUser] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  // Fetch edit request details
  const { data: editRequestData, isLoading, error } = useQuery(
    ['editRequest', editRequestId],
    async () => {
      const response = await api.get(`/edit-requests/${editRequestId}`);
      return response.data.editRequest;
    }
  );

  // Fetch reviewers for forwarding
  const { data: reviewersData } = useQuery(
    ['reviewers', editRequestData?.permit?.subdivisionId],
    async () => {
      const response = await api.get(`/users/reviewers/list?subdivisionId=${editRequestData?.permit?.subdivisionId}`);
      return response.data.reviewers;
    },
    {
      enabled: !!editRequestData?.permit?.subdivisionId
    }
  );

  const editRequest = editRequestData;

  // Determine user's review permissions
  const canReview = useMemo(() => {
    if (!editRequest || !user) return false;

    switch (editRequest.status) {
      case 'Pending_EAC_Review':
        return user.role === 'eac' && editRequest.assignedEAC === user.id;
      case 'Pending_SDO_Review':
        return user.role === 'sdo' && editRequest.assignedSDO === user.id;
      case 'Pending_ADC_Review':
        return user.role === 'adc' && editRequest.assignedADC === user.id;
      case 'Pending_DC_Approval':
        return user.role === 'dc';
      default:
        return false;
    }
  }, [editRequest, user]);

  // Check if ADC can give final approval
  const canAdcFinalApprove = useMemo(() => {
    if (!editRequest || !user) return false;
    return user.role === 'adc' &&
           editRequest.status === 'Pending_ADC_Review' &&
           editRequest.assignedADC === user.id &&
           user.subdivisionId &&
           Number(user.subdivisionId) === Number(editRequest.permit?.subdivisionId);
  }, [editRequest, user]);

  // Role hierarchy for filtering reviewers
  const ROLE_HIERARCHY = ['bo', 'eac', 'sdo', 'adc', 'dc'];

  // Get available reviewers with higher roles in same subdivision + all DC users
  const availableReviewers = useMemo(() => {
    if (!reviewersData || !user || !editRequestData?.permit) return [];

    const currentRoleIndex = ROLE_HIERARCHY.indexOf(user.role);
    const subdivisionId = editRequestData.permit.subdivisionId;

    // Filter users with higher roles in same subdivision + all DC users
    return reviewersData.filter(u => {
      const reviewerRoleIndex = ROLE_HIERARCHY.indexOf(u.role);

      // Must be a higher role
      if (reviewerRoleIndex <= currentRoleIndex) return false;

      // DC users always included (district-wide)
      if (u.role === 'dc') return true;

      // Other higher roles must be in same subdivision
      return u.subdivisionId === subdivisionId;
    });
  }, [reviewersData, user, editRequestData?.permit?.subdivisionId]);

  // Group reviewers by role for organized display
  const reviewersByRole = useMemo(() => {
    const grouped = {};
    const roleOrder = ['eac', 'sdo', 'adc', 'dc']; // Order for display

    availableReviewers.forEach(reviewer => {
      const role = reviewer.role;
      if (!grouped[role]) grouped[role] = [];
      grouped[role].push(reviewer);
    });

    // Return as array sorted by role hierarchy
    return roleOrder
      .filter(role => grouped[role] && grouped[role].length > 0)
      .map(role => ({
        level: role,
        label: role.toUpperCase(),
        users: grouped[role]
      }));
  }, [availableReviewers]);

  // Review mutation
  const reviewMutation = useMutation(
    async ({ action, comments, forwardToLevel, forwardToUser }) => {
      const response = await api.post(`/edit-requests/${editRequestId}/review`, {
        action,
        comments,
        forwardToLevel,
        forwardToUser
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['editRequest', editRequestId]);
        queryClient.invalidateQueries(['permit', editRequest?.permitId]);
        queryClient.invalidateQueries(['pendingEdit', editRequest?.permitId]);
        toast.success(data.message || 'Review submitted successfully!');
        setShowReviewDialog(false);
        if (data.editRequest?.status === 'Approved') {
          navigate(`/permits/${editRequest.permitId}`);
        }
      },
      onError: (error) => {
        if (error.response?.data?.requiresOTP) {
          setShowOTP(true);
        } else {
          toast.error(error.response?.data?.message || 'Failed to submit review');
        }
      }
    }
  );

  const handleReviewClick = (action) => {
    setReviewAction(action);
    setComments('');
    setForwardToLevel('');
    setForwardToUser('');
    setOtpVerified(false);
    setShowOTP(false);
    setShowReviewDialog(true);
  };

  const handleSubmitReview = () => {
    if (!comments.trim()) {
      toast.error('Please provide comments');
      return;
    }

    if (reviewAction === 'approve' && user.role !== 'dc') {
      if (!forwardToLevel || !forwardToUser) {
        toast.error('Please select a reviewer to forward to');
        return;
      }
    }

    // For final approval (DC or ADC final_approve), check OTP
    if ((user.role === 'dc' && reviewAction === 'approve') ||
        (user.role === 'adc' && reviewAction === 'final_approve')) {
      if (!otpVerified) {
        setShowOTP(true);
        return;
      }
    }

    reviewMutation.mutate({
      action: reviewAction,
      comments,
      forwardToLevel,
      forwardToUser
    });
  };

  const handleOTPVerified = (approvalComments) => {
    setOtpVerified(true);
    setShowOTP(false);
    setShowReviewDialog(false);
    // Submit the review with comments from OTP component
    reviewMutation.mutate({
      action: reviewAction,
      comments: approvalComments || comments,
      forwardToLevel,
      forwardToUser
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'Pending_EAC_Review': { label: 'Pending EAC Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'Pending_SDO_Review': { label: 'Pending SDO Review', color: 'bg-orange-100 text-orange-800', icon: Clock },
      'Pending_ADC_Review': { label: 'Pending ADC Review', color: 'bg-blue-100 text-blue-800', icon: Clock },
      'Pending_DC_Approval': { label: 'Pending DC Approval', color: 'bg-purple-100 text-purple-800', icon: Clock },
      'Approved': { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Rejected': { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
      'Cancelled': { label: 'Cancelled', color: 'bg-gray-100 text-gray-800', icon: XCircle }
    };
    const info = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: Clock };
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${info.color}`}>
        <Icon className="h-4 w-4 mr-1.5" />
        {info.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !editRequest) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Edit request not found or error loading data.
        </div>
      </div>
    );
  }

  const isPending = ['Pending_EAC_Review', 'Pending_SDO_Review', 'Pending_ADC_Review', 'Pending_DC_Approval'].includes(editRequest.status);

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to={`/permits/${editRequest.permitId}`} className="text-gray-600 hover:text-gray-800">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Edit Request
            </h1>
            <p className="text-gray-500">
              For Permit #{editRequest.permit?.permit_no}
            </p>
          </div>
        </div>
        {getStatusBadge(editRequest.status)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Changes Section */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Proposed Changes</h2>
            <EditRequestDiff
              editRequestId={editRequestId}
              originalValues={editRequest.originalValues}
              requestedChanges={editRequest.requestedChanges}
              documentChanges={editRequest.documentChanges}
            />
          </div>

          {/* History Timeline */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <History className="h-5 w-5" />
              Review History
            </h2>
            {editRequest.history && editRequest.history.length > 0 ? (
              <div className="space-y-4">
                {editRequest.history.map((entry, index) => (
                  <div key={entry.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${
                        entry.action === 'approved' ? 'bg-green-500' :
                        entry.action === 'rejected' ? 'bg-red-500' :
                        entry.action === 'created' ? 'bg-blue-500' :
                        'bg-yellow-500'
                      }`}></div>
                      {index < editRequest.history.length - 1 && (
                        <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {entry.action.charAt(0).toUpperCase() + entry.action.slice(1)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(entry.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {entry.performer?.fullName || 'System'} ({entry.performer?.role?.toUpperCase() || 'N/A'})
                      </p>
                      {entry.comments && (
                        <p className="text-sm text-gray-500 mt-1 italic">"{entry.comments}"</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No history available</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Request Info */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Request Information</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Initiated by:</span>
                <span className="font-medium">
                  {editRequest.initiator?.fullName || 'Unknown'}
                  <span className="text-gray-500 ml-1">({editRequest.initiatorRole?.toUpperCase()})</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Created:</span>
                <span>{formatDate(editRequest.createdAt)}</span>
              </div>
              {editRequest.reason && (
                <div className="border-t pt-3 mt-3">
                  <p className="text-gray-600 font-medium mb-1">Reason:</p>
                  <p className="text-gray-700">{editRequest.reason}</p>
                </div>
              )}
            </div>
          </div>

          {/* Permit Info */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Permit Details</h3>
            <div className="space-y-2 text-sm">
              <p>
                <span className="text-gray-600">Permit No:</span>{' '}
                <Link to={`/permits/${editRequest.permitId}`} className="text-primary-600 hover:underline font-medium">
                  {editRequest.permit?.permit_no}
                </Link>
              </p>
              <p>
                <span className="text-gray-600">Holder:</span>{' '}
                <span className="font-medium">{editRequest.permit?.name}</span>
              </p>
              <p>
                <span className="text-gray-600">Subdivision:</span>{' '}
                <span>{editRequest.permit?.subdivision?.name}</span>
              </p>
            </div>
          </div>

          {/* Review Actions */}
          {canReview && isPending && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Review Actions
              </h3>
              <div className="space-y-2">
                {/* ADC Final Approve Button */}
                {canAdcFinalApprove && (
                  <button
                    onClick={() => handleReviewClick('final_approve')}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Final Approve (ADC)
                  </button>
                )}

                {/* Approve / Escalate Button */}
                <button
                  onClick={() => handleReviewClick('approve')}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
                >
                  <ArrowRight className="h-4 w-4" />
                  {user?.role === 'dc' ? 'Approve & Merge' : (user?.role === 'adc' ? 'Escalate to DC' : 'Approve & Forward')}
                </button>

                <button
                  onClick={() => handleReviewClick('reject')}
                  className="w-full px-4 py-2 border border-red-500 text-red-700 rounded-lg hover:bg-red-50 flex items-center justify-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </button>
              </div>
            </div>
          )}

          {/* Completed Status Info */}
          {editRequest.status === 'Approved' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Changes Merged</span>
              </div>
              <p className="text-sm text-green-600 mt-2">
                Approved by {editRequest.approver?.fullName} on {formatDate(editRequest.approvedAt)}
              </p>
            </div>
          )}

          {editRequest.status === 'Rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700">
                <XCircle className="h-5 w-5" />
                <span className="font-medium">Request Rejected</span>
              </div>
              <p className="text-sm text-red-600 mt-2">
                Rejected by {editRequest.rejector?.fullName} on {formatDate(editRequest.rejectedAt)}
              </p>
              {editRequest.rejectionReason && (
                <p className="text-sm text-red-600 mt-1 italic">"{editRequest.rejectionReason}"</p>
              )}
              {user?.role === 'bo' && Number(user?.subdivisionId) === Number(editRequest.permit?.subdivisionId) && (
                <button
                  onClick={() => navigate(`/permits/${editRequest.permitId}/request-edit?reworkFrom=${editRequest.id}`)}
                  className="mt-3 w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <RotateCcw className="h-4 w-4" />
                  ReWork This Request
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Review Dialog */}
      {showReviewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {reviewAction === 'approve' && (user?.role === 'dc' ? 'Approve & Merge Changes' : 'Approve & Forward')}
              {reviewAction === 'final_approve' && 'Final Approval (ADC)'}
              {reviewAction === 'reject' && 'Reject Edit Request'}
            </h3>

            {/* Forward Selection (for non-DC approve) */}
            {reviewAction === 'approve' && user?.role !== 'dc' && (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={forwardToLevel}
                    onChange={(e) => {
                      setForwardToLevel(e.target.value);
                      setForwardToUser('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select Role</option>
                    {reviewersByRole.map(group => (
                      <option key={group.level} value={group.level}>
                        {group.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reviewer <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={forwardToUser}
                    onChange={(e) => setForwardToUser(e.target.value)}
                    disabled={!forwardToLevel}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Reviewer</option>
                    {availableReviewers
                      .filter(u => u.role === forwardToLevel)
                      .map(u => (
                        <option key={u.id} value={u.id}>
                          {u.fullName} ({u.designation || u.role.toUpperCase()})
                        </option>
                      ))
                    }
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select a reviewer with a higher role to forward this request.
                  </p>
                </div>
              </>
            )}

            {/* Comments - Hide when OTP is shown since OTP component has its own comments */}
            {!showOTP && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comments <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  placeholder="Enter your review comments..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            {/* OTP Section for DC/ADC Final Approval - Combined with comments */}
            {showOTP && (
              <div className="mb-4">
                <OTPVerificationCompact
                  permitId={editRequest.permitId}
                  onSuccess={handleOTPVerified}
                  onCancel={() => {
                    setShowOTP(false);
                    setShowReviewDialog(false);
                  }}
                  reviewComments={comments}
                  isLoading={reviewMutation.isLoading}
                />
              </div>
            )}

            {/* Action Buttons */}
            {!showOTP && (
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReviewDialog(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReview}
                  disabled={reviewMutation.isLoading}
                  className={`flex-1 px-4 py-2 rounded-lg text-white flex items-center justify-center gap-2 ${
                    reviewAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-primary-600 hover:bg-primary-700'
                  } disabled:opacity-50`}
                >
                  {reviewMutation.isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      {reviewAction === 'approve' && <CheckCircle className="h-4 w-4" />}
                      {reviewAction === 'final_approve' && <CheckCircle className="h-4 w-4" />}
                      {reviewAction === 'reject' && <XCircle className="h-4 w-4" />}
                      Submit
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditRequestView;
